import React from 'react';
import { Paper, Typography, Box, useTheme } from '@mui/material';
import { Star, Psychology, Edit, AutoAwesome, WavingHand } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAppStore } from '@/store';
import researcherCards from '@/config/researcherCards';

const CardPaper = styled(Paper)(({ theme }) => ({
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.8)' 
    : 'rgba(66, 66, 66, 0.8)',
  backdropFilter: 'blur(8px)',
  color: theme.palette.text.primary,
  borderRadius: '22px',
  padding: theme.spacing(1.5),
  boxShadow: '0 2px 12px rgba(0, 24, 39, 0.08)',
  border: theme.palette.mode === 'light'
    ? '1px solid rgba(0, 24, 39, 0.06)'
    : '1px solid rgba(255, 255, 255, 0.08)',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(0.5),
  '&:hover': {
    background: theme.palette.mode === 'light'
      ? 'rgba(255, 255, 255, 0.95)'
      : 'rgba(77, 77, 77, 0.95)',
    boxShadow: '0 4px 20px rgba(0, 24, 39, 0.12)',
    transform: 'translateY(-2px)',
    borderColor: theme.palette.mode === 'light'
      ? 'rgba(0, 24, 39, 0.12)'
      : 'rgba(255, 255, 255, 0.15)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.mode === 'light'
    ? 'rgba(0, 24, 39, 0.06)'
    : 'rgba(255, 255, 255, 0.08)',
}));

interface CardData {
  icon: React.ReactNode;
  description: string; // shown on the card
  prompt: string; // inserted into the chat input when clicked
}

const AgentCards: React.FC = () => {
  const setMessage = useAppStore(s => s.setMessage);
  const theme = useTheme();
  const selectedAgent = useAppStore(s => s.selectedAgent);
  const agentList = useAppStore(s => s.agentList);

  // Find the current agent and get its name for card lookup
  const currentAgent = agentList.find(a => a.id === selectedAgent);
  const agentName = currentAgent?.name?.toLowerCase() || '';

  // Default cards shown for any agent without specific cards
  const defaultAgentCards: CardData[] = [
    {
      icon: <WavingHand fontSize="small" />,
      description: 'Get started',
      prompt: 'What can you help me with?',
    },
    {
      icon: <AutoAwesome fontSize="small" />,
      description: 'Explore capabilities',
      prompt: 'What are your main capabilities and features?',
    },
    {
      icon: <Psychology fontSize="small" />,
      description: 'Ask a question',
      prompt: 'I have a question about ',
    },
  ];

  // Agent-specific cards mapping. Agents can have their own custom cards defined here.
  const agentCards: Record<string, CardData[]> = {
    // Researcher agent cards — edit researcherCards.tsx to customise
    researcher: researcherCards.map(c => ({
      icon: c.icon,
      description: c.shortTitle,
      prompt: c.prompt,
    })),
    // Rivet agent cards
    rivet: [
      {
        icon: <Psychology fontSize="small" />,
        description: 'Troubleshoot',
        prompt: 'Help me diagnose an issue with a machine or process: ',
      },
      {
        icon: <Star fontSize="small" />,
        description: 'Best practices',
        prompt: 'What are the best practices for maintaining this equipment? ',
      },
      {
        icon: <Edit fontSize="small" />,
        description: 'Rewrite SOP',
        prompt: 'Rewrite this SOP to be clearer and more concise: ',
      },
    ],
  };

  // Get cards for the selected agent (fallback to default cards if agent has no specific cards)
  // Use the agent's name (lowercase) for card lookup instead of the assistant ID
  const cards = selectedAgent 
    ? (agentCards[agentName] || defaultAgentCards) 
    : [];

  return (
    <Box 
      width="100%" 
      display="flex" 
      flexDirection="column" 
      alignItems="center"
      px={3}
      pt={4}
    >
      <Typography 
        variant="h3" 
        fontWeight={800} 
        color="text.primary" 
        mb={2} 
        sx={{ 
          letterSpacing: 1,
          textAlign: 'center',
          fontSize: { xs: '1.5rem', sm: '3rem' } // Smaller on mobile
        }}
      >
        Bossard AI Platform
      </Typography>
      
      <Box 
        display="flex" 
        alignItems="flex-start" 
        justifyContent="center"
        gap={1} 
        mb={6}
      >
        <Star sx={{ 
          color: theme.palette.warning.main,
          fontSize: { xs: '1.25rem', sm: '1.5rem' }
        }} />
        <Typography 
          variant="h5" 
          color="text.primary" 
          fontWeight={600}
          sx={{
            textAlign: 'center',
            fontSize: { xs: '1.1rem', sm: '1.5rem' },
            display: 'flex',
            alignItems: 'center'
          }}
        >
          Welcome, how can I help you?
        </Typography>
      </Box>

      <Box 
        display="flex" 
        flexWrap="wrap" 
        gap={3} 
        justifyContent="center"
        maxWidth={900}
        width="100%"
      >
        {cards.map((card, index) => (
          <Box key={index} sx={{ flex: '1 1 280px', maxWidth: 350 }}>
            <CardPaper elevation={1} onClick={() => setMessage(card.prompt)} sx={{ minHeight: 106 }}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <IconWrapper>
                  {card.icon}
                </IconWrapper>
                <Typography 
                  variant="subtitle1" 
                  fontWeight={600} 
                  color="text.primary"
                  sx={{ fontSize: '1.1rem', lineHeight: 1.2 }}
                >
                  {card.description}
                </Typography>
              </Box>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}
              >
                {card.prompt}
              </Typography>
            </CardPaper>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AgentCards;
